import { useCallback, useEffect, useState } from "react";
import { currentVersion } from "../../version";
import { compareVersions } from "../backoffice.utils";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { firestore } from "../firebase";
import { Alert, AlertTitle, Backdrop, Button } from "@mui/material";
import { autovioColors } from "./backofficeTheme";

export const MinAppVersionNotification = () => {
  const [isDeprecated, setIsDeprecated] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(firestore, `config/enforce_update`), (snapshot) => {
      const minVersion = snapshot.data()?.minimumAppVersions?.backoffice;
      if (minVersion) {
        const currentVersionLessMinimum = _isUpdateNeeded(minVersion);
        setIsDeprecated(currentVersionLessMinimum);
        if (currentVersionLessMinimum) {
          unsubscribe();
        }
      }
    });
    return unsubscribe;
  }, []);

  const getAndValidateMinVersion = useCallback(async () => {
    const minVersion = await _getMinVersion();
    if (typeof minVersion === "string") {
      setIsDeprecated(_isUpdateNeeded(minVersion));
    }
  }, []);

  const handleVisibilityChange = useCallback(async () => {
    if (document.visibilityState === "visible") {
      await getAndValidateMinVersion();
    }
  }, [getAndValidateMinVersion]);

  // Focus and visibility listeners to ensure version check is performed even if the tab was inactive when snapshot was updated.
  useEffect(() => {
    window.addEventListener("focus", getAndValidateMinVersion);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("focus", getAndValidateMinVersion);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [getAndValidateMinVersion, handleVisibilityChange]);

  if (location.hostname !== "backoffice.autovio.de" || !isDeprecated) {
    return null;
  }

  return (
    <Backdrop
      sx={(theme) => ({ zIndex: theme.zIndex.drawer + 1, alignItems: { xs: "flex-start", md: "flex-end" } })}
      open={isDeprecated && !isDismissed}
      onClick={() => setIsDismissed(true)}
    >
      <Alert
        severity="warning"
        variant="filled"
        sx={{ backgroundColor: autovioColors.red, mb: { xs: 0, md: "10vh" }, mt: { xs: "10vh", md: "0" } }}
        action={
          <Button variant="contained" color="primary" onClick={() => window.location.reload()}>
            Aktualisieren
          </Button>
        }
      >
        <AlertTitle>Neue Version verfügbar</AlertTitle>
        Bitte aktualisieren Sie die Seite, um die neueste Version zu erhalten
      </Alert>
    </Backdrop>
  );
};

const _getMinVersion = async () => {
  return (await getDoc(doc(firestore, "config/enforce_update"))).data()?.minimumAppVersions?.backoffice;
};

function _isUpdateNeeded(minVersion: string): boolean {
  if (currentVersion.endsWith("-dev")) {
    return false;
  }
  const result = compareVersions(minVersion, currentVersion);
  return result === 1;
}
